import {
  QUOTATION_SCHEMA_VERSION,
  type BillingCadence,
  type Quotation,
  type QuotationLineItem,
  type QuotationParty,
  type QuotationTemplateId,
} from "./model";

function createId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function emptyParty(): QuotationParty {
  return {
    name: "",
    address: "",
    email: "",
    phone: "",
  };
}

export function createManualLineItem(cadence: BillingCadence): QuotationLineItem {
  return {
    id: createId("line"),
    description: "",
    details: "",
    billingCadence: cadence,
    quantityMilli: 1_000,
    unit: "item",
    unitPriceMinor: null,
    valueStatus: "tbd",
    optional: false,
    included: true,
    provenance: {
      kind: "manual",
      label: "Manual price",
    },
  };
}

function baseQuotation(templateId: QuotationTemplateId, now: Date): Quotation {
  const timestamp = now.toISOString();
  return {
    id: createId("quotation"),
    schemaVersion: QUOTATION_SCHEMA_VERSION,
    templateId,
    createdAt: timestamp,
    updatedAt: timestamp,
    quotationNumber: "",
    issueDate: localDateString(now),
    validUntil: localDateString(addDays(now, 30)),
    currency: "PHP",
    currencyMinorUnits: 2,
    seller: emptyParty(),
    customer: emptyParty(),
    title: "Quotation",
    introduction: "",
    lineItems: [],
    adjustments: [],
    terms: [],
    preparedBy: "",
    closingNote: "",
    templateContext: { kind: "none" },
  };
}

export function createBlankQuotation(now = new Date()): Quotation {
  return baseQuotation("blank", now);
}

export function createSmartAttendanceQuotation(now = new Date()): Quotation {
  const quotation = baseQuotation("smart-attendance", now);
  return {
    ...quotation,
    title: "Smart Attendance subscription",
    lineItems: [
      {
        id: createId("smart-subscription"),
        description: "Smart Attendance subscription",
        details: "Employee tier and workbook-derived pricing require business review.",
        billingCadence: "recurring",
        quantityMilli: 1_000,
        unit: "month",
        unitPriceMinor: null,
        valueStatus: "tbd",
        optional: false,
        included: true,
        provenance: {
          kind: "needs-review",
          label: "Needs review",
          note: "The seed workbook does not establish approved tier boundaries or the face-registration pricing basis.",
        },
      },
    ],
    templateContext: {
      kind: "smart-attendance",
      employeeCount: null,
      pricingMode: "needs-review",
      sourceLabel: "Seed workbook - business review required",
    },
  };
}

export function createQuotationFromTemplate(
  templateId: QuotationTemplateId,
  now = new Date(),
): Quotation {
  return templateId === "smart-attendance"
    ? createSmartAttendanceQuotation(now)
    : createBlankQuotation(now);
}

export function switchSmartAttendanceToManualPricing(
  quotation: Quotation,
  now = new Date(),
): Quotation {
  if (quotation.templateContext.kind !== "smart-attendance") {
    return quotation;
  }

  return {
    ...quotation,
    updatedAt: now.toISOString(),
    templateContext: {
      ...quotation.templateContext,
      pricingMode: "manual",
    },
    lineItems: quotation.lineItems.map((lineItem) =>
      lineItem.provenance.kind === "needs-review"
        ? {
            ...lineItem,
            provenance: {
              kind: "manual",
              label: "Manual price",
            },
          }
        : lineItem,
    ),
  };
}
